import React, { useState, useEffect } from 'react';
import { Switch } from '@material-ui/core';
import CreateEditAccount from './createEdit';
import MsgBox from 'components/MsgBox/MsgBox';
import { useSelector, useDispatch } from 'react-redux';
import store from 'store';
import ResetPassword from './resetPassword';
import MenuAction from 'components/MenuAction';
import CustomTable from 'components/CustomTable';
import CustomTextField from 'components/Input/CustomTextField';
import { Search as SearchIcon } from '@material-ui/icons';
import CustomButton from 'components/CustomInput/Button/index';
import styled from 'styled-components/macro';
import PageSize from 'components/CustomTable/PageSize';
import CustomPagination from 'components/CustomPagination';
import {
    SYSTEM_EDIT_USER_ACCOUNT,
    SYSTEM_GET_USER_ACCOUNT,
    SYSTEM_GET_USER_GROUP,
    UPDATE_USER_ACCOUNT_STATUS,
    SYSTEM_UPDATE_USER_ACCOUNT,
    GET_EMAIL_DOMAIN_LIST,
} from 'store/key';
import { getTimeWithTimeZone } from 'utility/general';
import api from 'api/call';
import { setGlobalLoading } from 'store/global/reducer';
import useDialog from 'components/ConfirmDialog/useDialog';
import API from 'api/call';
import ViewDetails from './viewDetails.jsx';
import { USER_ACCOUNT_CREATED } from 'utility/frontendMessage';
import { getSystemMessage } from 'store/system/systemMessageReducer';

const STableContent = styled.div`
    margin-top: 15px;
`;

const SPaginationLayout = styled.div`
    margin-top: 20px;
`;

const UserAccount = (props) => {
    const dispatch = useDispatch();
    const showDialog = useDialog();
    const systemMessage = useSelector((state) => state.systemMessage.systemMessage);

    const rows = useSelector((state) => state.userAccount.userAccountList);
    const isLoading = useSelector((state) => state.userAccount.isLoading);
    const total = useSelector((state) => state.userAccount.total);
    const page = useSelector((state) => state.userAccount.page);
    const pageSize = useSelector((state) => state.userAccount.pageSize);
    const sort = useSelector((state) => state.userAccount.sort ?? 'createDtm');
    const isDesc = useSelector((state) => state.userAccount.isDesc ?? true);

    const [warningMsg, setWarningMsg] = useState(null);
    const getMenuActions = (row) => {
        return [
            {
                text: 'Edit',
                action: () => {
                    handleEdit(row);
                },
                disableConfirm: true,
            },
            {
                text: 'Reset password',
                action: () => {
                    handleResetPassword(row);
                },
                disableConfirm: true,
            },
            {
                text: 'Resend Activation',
                action: () => {
                    handleResendActivation(row);
                },
                disableConfirm: true,
                shouldHidden: (row, userRole) => {
                    return row.status !== 1 && row.status !== 6;
                },
            },
            {
                text: 'View Details',
                action: () => {
                    handleViewDetails(row);
                },
                disableConfirm: true,
            },
        ];
    };

    const columns = [
        {
            field: 'enable',
            headerName: 'Enable',
            disableItemSort: true,
            width: 80,
            renderCell: (params) => {
                return [0, 3].includes(params.row.status) ? (
                    <Switch
                        size="small"
                        color="primary"
                        checked={params.row.status === 0}
                        onChange={(e) => changeEnable(params.row)}
                    />
                ) : null;
            },
        },
        {
            field: 'action',
            disableItemSort: true,
            headerName: 'Action',
            width: 80,
            renderCell: (params) => {
                return <MenuAction actions={getMenuActions(params.row)} row={params.row} />;
            },
        },
        {
            field: 'email',
            headerName: 'Email',
            width: 200,
            onSort: (key, isDesc) => {
                sortUserAccounts(key, isDesc);
            },
        },
        {
            field: 'title',
            headerName: 'Title',
            width: 120,
            onSort: (key, isDesc) => {
                sortUserAccounts(key, isDesc);
            },
        },
        {
            field: 'fullName',
            headerName: 'Name',
            width: 140,
            onSort: (key, isDesc) => {
                sortUserAccounts(key, isDesc);
            },
        },
        {
            field: 'userGroup',
            headerName: 'User Groups',
            width: 200,
            onSort: (key, isDesc) => {
                sortUserAccounts(key, isDesc);
            },
        },
        {
            field: 'institution',
            headerName: 'Hospital',
            width: 120,
            onSort: (key, isDesc) => {
                sortUserAccounts(key, isDesc);
            },
        },
        {
            field: 'contact',
            headerName: 'Contact Number',
            width: 140,
            onSort: (key, isDesc) => {
                sortUserAccounts(key, isDesc);
            },
        },
        {
            field: 'justification',
            headerName: 'Justification',
            width: 120,
            onSort: (key, isDesc) => {
                sortUserAccounts(key, isDesc);
            },
        },
        {
            field: 'statusName',
            headerName: 'Status',
            sortable: false,
            width: 100,
            onSort: (key, isDesc) => {
                sortUserAccounts('statusName', isDesc);
            },
        },
        {
            field: 'lastLoginDtm',
            headerName: 'Last Login',
            sortable: false,
            width: 150,
            valueGetter: (params) =>
                params.getValue('lastLoginDtm')
                    ? getTimeWithTimeZone(params.getValue('lastLoginDtm')).format('YYYY-MM-DD HH:mm:ss')
                    : '-',
            onSort: (key, isDesc) => {
                sortUserAccounts(key, isDesc);
            },
        },
    ];
    const [openPopup, setOpenPopup] = useState(false);
    const [popupType, setPopupType] = useState('create');
    const [selectedRow, setSelectedRow] = useState(null);
    const [isResetPassword, setIsResetPassword] = useState(false);

    const [searchKeyword, setSearchKeyword] = useState('');

    const [formDataValid, setFormDataValid] = useState(false);
    const [forceShowError, setForceShowError] = useState(false);

    const [isView, setIsView] = useState(false);

    const getEmailDomains = () => {
        dispatch({
            type: GET_EMAIL_DOMAIN_LIST,
            payload: null,
        });
    };

    useEffect(() => {
        getEmailDomains();
        // eslint-disable-next-line
    }, []);

    const getUserAccounts = ({ page = 1, pageSize = 10 }) => {
        dispatch({
            type: SYSTEM_GET_USER_ACCOUNT,
            payload: {
                page,
                pageSize,
                param: searchKeyword,
                sort,
                isDesc,
            },
        });
    };

    const sortUserAccounts = (key, isDesc = true) => {
        const requestParams = {
            isDesc,
            page: 1,
            pageSize,
            sort: key,
            param: searchKeyword,
        };

        dispatch({
            type: SYSTEM_GET_USER_ACCOUNT,
            payload: requestParams,
        });
    };

    const getUserGroups = () => {
        dispatch({
            type: SYSTEM_GET_USER_GROUP,
            payload: null,
        });
    };

    useEffect(() => {
        getUserAccounts({ pageSize });
        getUserGroups();
        // eslint-disable-next-line
    }, []);

    const handleResendActivation = (row) => {
        setWarningMsg({
            message: systemMessage.inquirySendActivation,
            footerActions: [
                {
                    label: 'Cancel',
                    color: 'default',
                    clicked: () => {
                        setWarningMsg(null);
                    },
                },
                {
                    label: 'Confirm',
                    clicked: () => {
                        reSendActivation(row.email);
                    },
                },
            ],
        });
    };

    const reSendActivation = (email) => {
        setGlobalLoading(true);
        API.user
            .resendActivationEmail({ email: email })
            .then((res) => {
                setWarningMsg({
                    type: 'confirm',
                    message: systemMessage.sendEmail_activation,
                    footerActions: [
                        {
                            label: 'Confirm',
                            clicked: () => {
                                setWarningMsg(null);
                            },
                        },
                    ],
                });
            })
            .catch(() => {})
            .finally(() => setGlobalLoading(false));
    };

    const handleResetPassword = (row) => {
        setIsResetPassword(true);
        setSelectedRow(row);
    };

    const handleCreate = () => {
        setOpenPopup(true);
        setSelectedRow(null);
        setPopupType('create');
    };

    const createUser = async (row) => {
        setGlobalLoading(true);
        api.userAccount
            .createUserAccount(row)
            .then(() => {
                setOpenPopup(false);
                getUserAccounts({ pageSize });
                showDialog({
                    description: USER_ACCOUNT_CREATED,
                    disableCancelButton: true,
                });
            })
            .finally(() => setGlobalLoading(false));
    };

    const updateUser = (row) => {
        setGlobalLoading(true);
        dispatch({
            type: SYSTEM_UPDATE_USER_ACCOUNT,
            payload: {
                row,
                callback: () => {
                    setGlobalLoading(false);
                    setOpenPopup(false);
                    showDialog({
                        description: systemMessage.updatedAccount,
                        disableCancelButton: true,
                    });
                    getUserAccounts({ page, pageSize });
                },
            },
        });
    };

    const handleEdit = (row) => {
        setSelectedRow(row);
        setOpenPopup(true);
        setPopupType('edit');
    };

    const editRow = (row) => {
        store.dispatch({
            type: SYSTEM_EDIT_USER_ACCOUNT,
            payload: row,
        });
    };

    const updateStatus = (row) => {
        dispatch({
            type: UPDATE_USER_ACCOUNT_STATUS,
            payload: {
                row: { status: row.status ? 0 : 3, id: row.id },
                callback: () => {
                    dispatch({
                        type: SYSTEM_GET_USER_ACCOUNT,
                        payload: {
                            page,
                            pageSize,
                            param: searchKeyword,
                            sort,
                            isDesc,
                        },
                    });
                },
            },
        });

        setWarningMsg(null);
    };

    const changeEnable = (row) => {
        setWarningMsg({
            type: 'confirm',
            message: getSystemMessage('disableEnableAcc', [row.status !== 3 ? 'disable' : 'enable']),
            footerActions: [
                {
                    label: 'Cancel',
                    color: 'default',
                    clicked: () => {
                        setWarningMsg(null);
                    },
                },
                {
                    label: 'Confirm',
                    clicked: () => {
                        updateStatus(row);
                    },
                },
            ],
        });
    };

    const handleViewDetails = (row) => {
        setSelectedRow(row);
        setIsView(true);
    };

    const handleSearch = () => {
        setForceShowError(true);
        if (formDataValid) {
            getUserAccounts({ pageSize });
        }
    };

    return (
        <div style={{ padding: 15 }}>
            {warningMsg && (
                <MsgBox
                    type={warningMsg.type}
                    message={warningMsg.message}
                    footerActions={warningMsg.footerActions}
                    title={warningMsg.title}
                />
            )}
            {openPopup && (
                <CreateEditAccount
                    popupType={popupType}
                    editRow={editRow}
                    rowData={selectedRow}
                    openPopup={openPopup}
                    setOpenPopup={setOpenPopup}
                    createUser={createUser}
                    updateUser={updateUser}
                />
            )}
            {isResetPassword && (
                <ResetPassword
                    rowData={selectedRow}
                    setOpenPopup={setIsResetPassword}
                    openPopup={isResetPassword}
                    setWarningMsg={setWarningMsg}
                    systemMessage={systemMessage}
                    onPasswordUpdated={() => {
                        getUserAccounts({ page, pageSize });
                    }}
                />
            )}
            {isView && <ViewDetails rowData={selectedRow} openPopup={isView} setOpenPopup={setIsView} />}

            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex' }}>
                    <CustomButton onClick={handleCreate}>Create</CustomButton>
                    <div style={{ width: 300, margin: '0 20px' }}>
                        <CustomTextField
                            label="Search"
                            name="search"
                            validators={['disableChinese']}
                            isValidValue={(valid) => setFormDataValid(valid)}
                            forceErrorDisplay={forceShowError}
                            value={searchKeyword}
                            onChange={(e) => setSearchKeyword(e.target.value)}
                            maxLength={255}
                        />
                    </div>
                    <CustomButton onClick={handleSearch}>
                        <SearchIcon />
                    </CustomButton>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <PageSize
                        label="Page Size"
                        value={pageSize}
                        onChange={(value) => {
                            getUserAccounts({ pageSize: value });
                        }}
                    />
                </div>
            </div>

            <STableContent>
                <CustomTable
                    columns={columns}
                    allowResize
                    fullWidthTable
                    loading={isLoading}
                    rows={rows}
                    isSortable
                    skipMaxHeight={false}
                    maxHeight={600}
                />
            </STableContent>
            <SPaginationLayout>
                <CustomPagination
                    page={page}
                    pageSize={pageSize}
                    total={total}
                    isLoading={isLoading}
                    onPageChange={(page) => {
                        getUserAccounts({ page, pageSize });
                    }}
                />
            </SPaginationLayout>
        </div>
    );
};

export default UserAccount;
